// Toast — left-stripe pattern. A `<ToastContainer>` orchestrator can land
// later; FE-030.5 ships the standalone toast that renders correctly.
//
// Variants:
//   success — Sage stripe + Sage text
//   error   — Ember stripe + Ember text
//   info    — Stone stripe + text-secondary text
//
// Auto-dismiss after `autoDismissMs` (default 5000). Pass `0` to disable.

import { X } from "lucide-react";
import { useEffect, type ReactNode } from "react";

import { cn } from "./cn";

export type ToastVariant = "success" | "error" | "info";

export interface ToastProps {
  variant?: ToastVariant;
  title: ReactNode;
  description?: ReactNode;
  onDismiss?: () => void;
  /** Auto-dismiss timer in ms. 0 disables auto-dismiss. Default 5000. */
  autoDismissMs?: number;
  className?: string;
}

const VARIANT_STRIPE: Record<ToastVariant, string> = {
  success: "border-l-sage",
  error: "border-l-ember",
  info: "border-l-text-tertiary",
};

const VARIANT_TEXT: Record<ToastVariant, string> = {
  success: "text-sage-ink",
  error: "text-ember-ink",
  info: "text-text-secondary",
};

export function Toast({
  variant = "info",
  title,
  description,
  onDismiss,
  autoDismissMs = 5000,
  className,
}: ToastProps) {
  useEffect(() => {
    if (!onDismiss) return;
    if (autoDismissMs <= 0) return;
    const id = window.setTimeout(onDismiss, autoDismissMs);
    return () => window.clearTimeout(id);
  }, [autoDismissMs, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      data-variant={variant}
      className={cn(
        "fixed top-4 right-4 z-50 flex w-80 items-start gap-3",
        "rounded-md border border-border-subtle border-l-[3px] bg-raised px-4 py-3 shadow-toast",
        VARIANT_STRIPE[variant],
        className,
      )}
    >
      <div className="flex-1">
        <p className={cn("text-sm font-semibold", VARIANT_TEXT[variant])}>{title}</p>
        {description && (
          <p className="mt-1 text-sm text-text-secondary">{description}</p>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-text-tertiary transition-fast ease-out hover:bg-recessed hover:text-text-primary"
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
